import {useCallback, useEffect, useRef, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {type Room} from 'matrix-js-sdk'

import {logger} from '#/logger'
import {useMatrixContext} from '#/state/matrix'
import {useDirectRooms} from '#/state/matrix/hooks/useDirectRooms'
import {
  extractOtherMemberDids,
  type MatrixRoomSummary,
  roomToSummary,
} from '#/state/matrix/utils'
import {RQKEY as PROFILE_RQKEY} from '#/state/queries/profile'
import {useProfileQuery} from '#/state/queries/profile'
import {useAgent} from '#/state/session'
import {useSession} from '#/state/session'

type AllowIncoming = 'all' | 'none' | 'following'

export function useMatrixChatRequests() {
  const {client, isConnected} = useMatrixContext()
  const {currentAccount} = useSession()
  const agent = useAgent()
  const queryClient = useQueryClient()
  const {isDirectRoom, getDirectRoomUserId} = useDirectRooms()
  const [pendingRequests, setPendingRequests] = useState<MatrixRoomSummary[]>(
    [],
  )
  const processingRef = useRef(false)

  const {data: myProfile} = useProfileQuery({did: currentAccount?.did})
  const allowIncoming: AllowIncoming =
    (myProfile?.associated?.chat?.allowIncoming as AllowIncoming) ?? 'following'

  const processInvites = useCallback(async () => {
    if (!client || !currentAccount || processingRef.current) return
    processingRef.current = true

    // Safety timeout: reset processingRef after 30s to prevent permanent hang
    const timeout = setTimeout(() => {
      if (processingRef.current) {
        logger.warn('processInvites timed out after 30s, resetting guard')
        processingRef.current = false
      }
    }, 30_000)

    try {
      const myUserId = client.getUserId() || ''
      const inviteRooms = client
        .getRooms()
        .filter(r => r.getMyMembership() === 'invite')

      if (inviteRooms.length === 0) {
        setPendingRequests([])
        return
      }

      if (allowIncoming === 'none') {
        // Silently reject all invites
        for (const room of inviteRooms) {
          client.leave(room.roomId).catch(err => {
            logger.warn('Failed to reject invite', {safeMessage: err})
          })
        }
        setPendingRequests([])
        return
      }

      const newPending: MatrixRoomSummary[] = []

      for (const room of inviteRooms) {
        const otherDids = extractOtherMemberDids(room, myUserId)
        const inviterDid = otherDids[0]

        if (!inviterDid) {
          // Can't determine inviter, silently reject
          client.leave(room.roomId).catch(err => {
            logger.warn('Failed to reject unknown invite', {safeMessage: err})
          })
          continue
        }

        // Fetch inviter's profile to check if we follow them
        let iFollowThem = false
        try {
          const profile = await queryClient.fetchQuery({
            queryKey: PROFILE_RQKEY(inviterDid),
            queryFn: async () => {
              const res = await agent.getProfile({actor: inviterDid})
              return res.data
            },
            staleTime: 15_000,
          })
          iFollowThem = !!profile.viewer?.following
        } catch {
          // Profile fetch failed — treat as not followed (safe default)
        }

        if (allowIncoming === 'following') {
          if (iFollowThem) {
            // Auto-accept: user follows inviter
            client.joinRoom(room.roomId).catch(err => {
              logger.warn('Failed to auto-accept invite', {safeMessage: err})
            })
          } else {
            // Silently reject: inviter not followed, shouldn't have reached us
            client.leave(room.roomId).catch(err => {
              logger.warn('Failed to reject invite', {safeMessage: err})
            })
          }
        } else if (allowIncoming === 'all') {
          if (iFollowThem) {
            // Auto-accept: user follows inviter
            client.joinRoom(room.roomId).catch(err => {
              logger.warn('Failed to auto-accept invite', {safeMessage: err})
            })
          } else {
            // Hold as pending request
            newPending.push(
              roomToSummary(room, myUserId, isDirectRoom, getDirectRoomUserId),
            )
          }
        }
      }

      setPendingRequests(newPending)
    } finally {
      clearTimeout(timeout)
      processingRef.current = false
    }
  }, [
    client,
    currentAccount,
    allowIncoming,
    agent,
    queryClient,
    isDirectRoom,
    getDirectRoomUserId,
  ])

  const acceptRequest = useCallback(
    async (roomId: string) => {
      if (!client) return
      await client.joinRoom(roomId)
      setPendingRequests(prev => prev.filter(r => r.roomId !== roomId))
    },
    [client],
  )

  const rejectRequest = useCallback(
    async (roomId: string) => {
      if (!client) return
      await client.leave(roomId)
      setPendingRequests(prev => prev.filter(r => r.roomId !== roomId))
    },
    [client],
  )

  useEffect(() => {
    if (!client || !isConnected) return

    processInvites()

    const onMembership = (_room: Room, membership: string) => {
      if (membership === 'invite') {
        processInvites()
      }
    }
    client.on('Room.myMembership' as any, onMembership)

    return () => {
      client.removeListener('Room.myMembership' as any, onMembership)
    }
  }, [client, isConnected, processInvites])

  return {
    pendingRequests,
    acceptRequest,
    rejectRequest,
    refreshRequests: processInvites,
  }
}
